/**
 * Wallet Discovery (EIP-6963)
 *
 * Discovers all installed browser wallet extensions using the EIP-6963 standard.
 * Falls back to legacy window.ethereum for older wallets.
 *
 * @see https://eips.ethereum.org/EIPS/eip-6963
 */

import type {
  DetectedWallet,
  EIP6963AnnounceProviderEvent,
  EIP1193Provider,
} from './types'
import { logger } from '../../utils/logger'

// Store discovered wallets
const discoveredWallets: Map<string, DetectedWallet> = new Map()

// Callbacks for wallet discovery updates
const discoveryCallbacks: Set<(wallets: DetectedWallet[]) => void> = new Set()

// Whether discovery has been initialized
let isInitialized = false

/**
 * Handle wallet announcement event
 */
function handleAnnouncement(event: EIP6963AnnounceProviderEvent): void {
  const { info, provider } = event.detail

  // Use uuid as unique identifier
  if (!discoveredWallets.has(info.uuid)) {
    discoveredWallets.set(info.uuid, {
      info,
      provider,
      isLegacy: false,
    })

    // Notify all listeners
    notifyListeners()
  }
}

/**
 * Add legacy window.ethereum as fallback
 */
function addLegacyProvider(): void {
  if (typeof window === 'undefined' || !window.ethereum) return

  // Check if we already have this provider via EIP-6963
  // Most modern wallets will announce themselves, so we check for duplicates
  const ethereum = window.ethereum as unknown as EIP1193Provider & {
    isMetaMask?: boolean
    isCoinbaseWallet?: boolean
    isRabby?: boolean
    isBraveWallet?: boolean
  }

  // Generate a legacy identifier
  const legacyId = 'legacy-ethereum'

  // Don't add if we already have wallets discovered via EIP-6963
  // (they're likely the same wallet)
  if (discoveredWallets.size > 0) return

  // Create a basic info object for the legacy provider
  let name = 'Browser Wallet'
  let rdns = 'unknown.wallet'

  if (ethereum.isMetaMask) {
    name = 'MetaMask'
    rdns = 'io.metamask'
  } else if (ethereum.isCoinbaseWallet) {
    name = 'Coinbase Wallet'
    rdns = 'com.coinbase.wallet'
  } else if (ethereum.isRabby) {
    name = 'Rabby'
    rdns = 'io.rabby'
  } else if (ethereum.isBraveWallet) {
    name = 'Brave Wallet'
    rdns = 'com.brave.wallet'
  }

  const legacyWallet: DetectedWallet = {
    info: {
      uuid: legacyId,
      name,
      icon: '', // No icon for legacy
      rdns,
    },
    provider: ethereum,
    isLegacy: true,
  }

  discoveredWallets.set(legacyId, legacyWallet)
  notifyListeners()
}

/**
 * Notify all discovery listeners
 */
function notifyListeners(): void {
  const wallets = Array.from(discoveredWallets.values())
  discoveryCallbacks.forEach((callback) => callback(wallets))
}

/**
 * Initialize wallet discovery
 * Call this once on app startup
 */
export function initWalletDiscovery(): void {
  if (typeof window === 'undefined') return
  if (isInitialized) return

  isInitialized = true

  // Listen for EIP-6963 announcements
  window.addEventListener(
    'eip6963:announceProvider',
    handleAnnouncement as EventListener
  )

  // Request providers to announce themselves
  window.dispatchEvent(new Event('eip6963:requestProvider'))

  // Give EIP-6963 wallets time to announce, then add legacy fallback
  setTimeout(() => {
    addLegacyProvider()
  }, 100)
}

/**
 * Get all discovered wallets
 */
export function getDiscoveredWallets(): DetectedWallet[] {
  return Array.from(discoveredWallets.values())
}

/**
 * Subscribe to wallet discovery updates
 * Returns unsubscribe function
 */
export function onWalletsDiscovered(
  callback: (wallets: DetectedWallet[]) => void
): () => void {
  discoveryCallbacks.add(callback)

  // Immediately call with current wallets if any
  if (discoveredWallets.size > 0) {
    callback(Array.from(discoveredWallets.values()))
  }

  // Return unsubscribe function
  return () => {
    discoveryCallbacks.delete(callback)
  }
}

/**
 * Get a specific wallet by its rdns identifier
 */
export function getWalletByRdns(rdns: string): DetectedWallet | undefined {
  for (const wallet of discoveredWallets.values()) {
    if (wallet.info.rdns === rdns) {
      return wallet
    }
  }
  return undefined
}

/**
 * Check if any wallets are available
 */
export function hasWallets(): boolean {
  return discoveredWallets.size > 0 ||
    (typeof window !== 'undefined' && !!window.ethereum)
}

/**
 * Clear all discovered wallets (for testing)
 */
export function clearDiscoveredWallets(): void {
  discoveredWallets.clear()
  isInitialized = false
}

/**
 * Check if an address is a smart contract wallet (EIP-1271)
 * Smart contract wallets like Safe, Argent, etc. produce non-deterministic signatures
 *
 * @param address - The wallet address to check
 * @param provider - An EIP-1193 provider to query
 * @returns true if the address has contract code deployed
 */
export async function isSmartContractWallet(
  address: string,
  provider: EIP1193Provider
): Promise<boolean> {
  try {
    const code = await provider.request({
      method: 'eth_getCode',
      params: [address, 'latest'],
    }) as string

    // If code is just '0x' or '0x0', it's an EOA (externally owned account)
    // If it has more data, it's a smart contract
    return code !== '0x' && code !== '0x0'
  } catch (error) {
    logger.warn('[Discovery] Failed to check if smart contract wallet:', error)
    // Default to false (assume EOA) if we can't check
    return false
  }
}
